import type { DeviceController } from "./device-interface"
import {
  ActuatorType,
  ButtplugBrowserWebsocketClientConnector,
  ButtplugClient,
  LinearCmd,
  VectorSubcommand,
  type ButtplugClientDevice,
} from "buttplug"

export class ButtplugDevice implements DeviceController {
  type = "buttplug" as const
  private client: ButtplugClient | null = null
  private device: ButtplugClientDevice | null = null
  private serverUrl = "ws://localhost:12345"
  private isClientConnected = false
  private isConnected = false
  private connectionMonitorInterval: NodeJS.Timeout | null = null
  private lastError: string | null = null
  private strokeMin = 0
  private strokeMax = 100
  private oscillateSpeed = 50
  private oscillateDirection: 0 | 100 = 100
  private oscillateTimer: ReturnType<typeof setTimeout> | null = null

  constructor() {
    this.client = new ButtplugClient("Goonru")
  }

  getLastConnectionError(): string | null {
    return this.lastError
  }

  private getIntifaceConnectionError(): string {
    const isHttpsPage = typeof window !== "undefined" && window.location.protocol === "https:"
    const isInsecureWs = this.serverUrl.startsWith("ws://")

    return isHttpsPage && isInsecureWs
      ? `Could not reach Intiface Central at ${this.serverUrl}. Browsers block an insecure ws:// connection from a secure https:// page -- this page needs to be loaded over http:// (e.g. during local dev) for Intiface's default server to work, since Intiface Central does not serve wss://.`
      : `Could not reach Intiface Central at ${this.serverUrl}. Make sure Intiface Central is running on this computer and its server has been started (the "Start Server" toggle inside Intiface Central).`
  }

  private async ensureClientConnected(): Promise<boolean> {
    if (!this.client) {
      this.client = new ButtplugClient("Goonru")
    }

    if (this.isClientConnected) return true

    try {
      const connector = new ButtplugBrowserWebsocketClientConnector(this.serverUrl)
      await this.client.connect(connector)
      this.isClientConnected = true
      return true
    } catch (error) {
      // A browser page loaded over https:// cannot open a plain ws://
      // connection to localhost (mixed-content blocking) -- this is the
      // single most common real-world cause of "nothing happens" here,
      // so call it out by name rather than leaving it as a generic
      // connect failure the user has to reverse-engineer.
      this.lastError = this.getIntifaceConnectionError()
      return false
    }
  }

  private async openAndScan(
    timeoutMs: number,
    shouldStopEarly?: (device: ButtplugClientDevice) => boolean,
  ): Promise<{ found: ButtplugClientDevice[]; timedOut: boolean; connected: boolean }> {
    const connected = await this.ensureClientConnected()
    if (!connected || !this.client) {
      return { found: [], timedOut: false, connected: false }
    }

    // client.connect() already runs an internal requestDeviceList() as part
    // of its handshake and emits "deviceadded" for every device Intiface had
    // already connected *before* we get here -- our own "deviceadded"
    // listener below is only attached after this function resolves, so it
    // can never catch those. That silently hid the single most common real
    // case: the user already paired their device inside Intiface Central
    // (as its own UI shows it connected), then just opens this app -- no
    // new scan ever happens, so it was never "found". client.devices is the
    // already-synced list from that same handshake; check it first.
    const found: ButtplugClientDevice[] = [...this.client.devices]
    if (found.length > 0 && (!shouldStopEarly || found.some(shouldStopEarly))) {
      return { found, timedOut: false, connected: true }
    }

    let timedOut = false

    await this.client.startScanning()
    try {
      await new Promise<void>((resolve) => {
        let settled = false
        let timeout: ReturnType<typeof setTimeout>

        const finish = (didTimeOut: boolean) => {
          if (settled) return
          settled = true
          timedOut = didTimeOut
          clearTimeout(timeout)
          this.client!.removeListener("deviceadded", onDeviceAdded)
          resolve()
        }

        const onDeviceAdded = (dev: ButtplugClientDevice) => {
          found.push(dev)
          if (shouldStopEarly?.(dev)) finish(false)
        }

        timeout = setTimeout(() => finish(true), timeoutMs)
        this.client!.addListener("deviceadded", onDeviceAdded)
      })
    } finally {
      await this.client.stopScanning()
    }

    return { found, timedOut, connected: true }
  }

  async connect(): Promise<boolean> {
    this.lastError = null
    try {
      if (this.device && this.isConnected) return true

      const isConnectCompatible = (dev: ButtplugClientDevice) =>
        Boolean(dev.messageAttributes.LinearCmd || dev.messageAttributes.ScalarCmd)

      const scan = await this.openAndScan(8000, isConnectCompatible)
      if (!scan.connected) return false

      // Accept linear OR scalar devices for direct connection. Track every
      // device name seen even if it is filtered out below, so a failure can
      // distinguish "Intiface saw unsupported hardware" from "Intiface saw
      // nothing at all".
      const compatibleDevice = scan.found.find(isConnectCompatible) ?? null
      if (compatibleDevice) {
        this.device = compatibleDevice
        this.isConnected = true
        console.log("[v0] Buttplug device connected:", this.device.name)
        return true
      }

      const seenDeviceNames = scan.found.map((dev) => dev.name)
      this.lastError = seenDeviceNames.length > 0
        ? `Connected to Intiface Central and saw ${seenDeviceNames.join(", ")}, but it doesn't support stroke or vibration control (no LinearCmd/ScalarCmd) -- that device type isn't supported here yet.`
        : "Connected to Intiface Central, but it reported zero devices within 8 seconds. This means the device itself isn't connected inside Intiface Central yet -- open Intiface Central, confirm your device shows as connected THERE first (not just plugged in/powered on), then click Connect here again."
      return false
    } catch (error) {
      this.lastError = error instanceof Error ? error.message : "Buttplug connection failed for an unknown reason."
      console.error("[v0] Buttplug connection failed:", error)
      return false
    }
  }

  async setPosition(position: number, duration?: number): Promise<boolean> {
    if (!this.device || !this.isConnected) return false

    try {
      const normalized = position / 100

      // Prefer LinearCmd for strokers (Handy via Intiface, OSR2, Kiiroo, etc.)
      // buttplug v3's client.linear() takes [Position, Duration] tuples, not the
      // v1/v2 {Index, Duration, Position} object shape — the object form silently
      // sent undefined for both fields (LinearCmd.Create reads tuple[0]/tuple[1]).
      // Feature index is implied by array position, so a single-tuple array is
      // always index 0, which is exactly the main stroke axis here.
      if (this.device.messageAttributes.LinearCmd) {
        await this.device.linear([[normalized, duration || 200]])
        return true
      }

      // Fall back to ScalarCmd with Position actuator for other devices
      if (this.device.messageAttributes.ScalarCmd) {
        await this.device.scalar([{ Index: 0, Scalar: normalized, ActuatorType: ActuatorType.Position }])
        return true
      }

      return false
    } catch (error) {
      console.error("[v0] Buttplug setPosition failed:", error)
      return false
    }
  }

  async setVibration(intensity: number): Promise<boolean> {
    if (!this.device || !this.isConnected) return false
    try {
      const normalized = intensity / 100
      if (this.device.messageAttributes.ScalarCmd) {
        await this.device.scalar([{ Index: 0, Scalar: normalized, ActuatorType: ActuatorType.Vibrate }])
        return true
      }
      return false
    } catch (error) {
      console.error("[v0] Buttplug setVibration failed:", error)
      return false
    }
  }

  async setRotation(speed: number): Promise<boolean> {
    if (!this.device || !this.isConnected) return false
    try {
      const normalized = Math.abs((speed - 50) / 50) // 0-1 speed from center
      const clockwise = speed >= 50
      // Same v1/v2-vs-v3 shape mismatch as setPosition — rotate() takes
      // [Speed, Clockwise] tuples.
      if (this.device.messageAttributes.RotateCmd) {
        await this.device.rotate([[normalized, clockwise]])
        return true
      }
      return false
    } catch (error) {
      console.error("[v0] Buttplug setRotation failed:", error)
      return false
    }
  }

  async setOscillation(speed: number): Promise<boolean> {
    if (!this.device || !this.isConnected) return false
    try {
      const normalized = speed / 100
      if (this.device.messageAttributes.ScalarCmd) {
        await this.device.scalar([{ Index: 0, Scalar: normalized, ActuatorType: ActuatorType.Oscillate }])
        return true
      }
      return false
    } catch (error) {
      console.error("[v0] Buttplug setOscillation failed:", error)
      return false
    }
  }

  async setConstrict(level: number): Promise<boolean> {
    if (!this.device || !this.isConnected) return false
    try {
      const normalized = level / 100
      if (this.device.messageAttributes.ScalarCmd) {
        await this.device.scalar([{ Index: 0, Scalar: normalized, ActuatorType: ActuatorType.Constrict }])
        return true
      }
      return false
    } catch (error) {
      console.error("[v0] Buttplug setConstrict failed:", error)
      return false
    }
  }

  // OSR2/SR6 multi-axis - each axis is a separate LinearCmd feature index
  // L0=0 (stroke, handled by setPosition), L1=1 (surge), L2=2 (sway)
  // R0=3 (twist, handled by setRotation), R1=4 (roll), R2=5 (pitch)

  // buttplug v3's device.linear() convenience method takes an array of
  // [Position, Duration] tuples whose FEATURE INDEX is implied by array
  // position (element 0 -> feature 0, element 1 -> feature 1, ...) — there is
  // no way to address feature index 1/2/4/5 directly through it without also
  // re-sending every lower index. Surge/sway/roll/pitch target those non-zero
  // indices independently of the main stroke axis, so they build the LinearCmd
  // message directly (same v3 classes the convenience method uses internally)
  // and send it with the real device/feature index instead.
  private async sendLinearAxis(index: number, position: number, duration: number): Promise<boolean> {
    if (!this.device) return false
    const msg = new LinearCmd(
      [new VectorSubcommand(index, position / 100, duration)],
      this.device.index,
    )
    await this.device.sendExpectOk(msg)
    return true
  }

  async setSurge(position: number, duration = 200): Promise<boolean> {
    if (!this.device || !this.isConnected) return false
    try {
      if (this.device.messageAttributes.LinearCmd) {
        return await this.sendLinearAxis(1, position, duration)
      }
      return false
    } catch (error) {
      console.error("[v0] Buttplug setSurge failed:", error)
      return false
    }
  }

  async setSway(position: number, duration = 200): Promise<boolean> {
    if (!this.device || !this.isConnected) return false
    try {
      if (this.device.messageAttributes.LinearCmd) {
        return await this.sendLinearAxis(2, position, duration)
      }
      return false
    } catch (error) {
      console.error("[v0] Buttplug setSway failed:", error)
      return false
    }
  }

  async setRoll(position: number, duration = 200): Promise<boolean> {
    if (!this.device || !this.isConnected) return false
    try {
      if (this.device.messageAttributes.LinearCmd) {
        return await this.sendLinearAxis(4, position, duration)
      }
      return false
    } catch (error) {
      console.error("[v0] Buttplug setRoll failed:", error)
      return false
    }
  }

  async setPitch(position: number, duration = 200): Promise<boolean> {
    if (!this.device || !this.isConnected) return false
    try {
      if (this.device.messageAttributes.LinearCmd) {
        return await this.sendLinearAxis(5, position, duration)
      }
      return false
    } catch (error) {
      console.error("[v0] Buttplug setPitch failed:", error)
      return false
    }
  }

  async stop(): Promise<boolean> {
    if (!this.device || !this.isConnected) return false

    try {
      await this.device.stop()
      return true
    } catch (error) {
      console.error("[v0] Buttplug stop failed:", error)
      return false
    }
  }

  setConnectionKey(key: string) {
    this.serverUrl = key
  }

  getConnectionKey(): string | null {
    return this.serverUrl
  }

  async checkConnection(): Promise<boolean> {
    return this.isConnected && this.device !== null
  }

  async getInfo(): Promise<any> {
    if (!this.device) return null
    return {
      name: this.device.name,
      index: this.device.index,
      messageAttributes: this.device.messageAttributes,
    }
  }

  async getStatus(): Promise<any> {
    return {
      connected: this.isConnected,
      deviceName: this.device?.name || null,
    }
  }

  async testMovement(): Promise<boolean> {
    const success1 = await this.setPosition(100, 500)
    await new Promise((resolve) => setTimeout(resolve, 500))
    const success2 = await this.setPosition(0, 500)
    return success1 && success2
  }

  // What the connected device can actually do, so the manual-control UI can
  // show length/position sliders for a stroker and an intensity slider for a
  // vibrator instead of one generic control guessed from the device type.
  getCapabilities(): { linear: boolean; vibrate: boolean; rotate: boolean } {
    const attrs = this.device?.messageAttributes
    return {
      linear: Boolean(attrs?.LinearCmd),
      vibrate: Boolean(attrs?.ScalarCmd),
      rotate: Boolean(attrs?.RotateCmd),
    }
  }

  // ── Manual / freeplay control ────────────────────────────────────────────
  // Unlike Handy's native HAMP mode (tell the device a velocity, it
  // oscillates on its own), Buttplug's LinearCmd is a single discrete "move
  // to X over duration Y" command with no continuous-motion mode -- so a
  // linear device's "Start" has to run its own client-side loop, alternating
  // setPosition calls between strokeMin/strokeMax, timed from speed. Without
  // this, manualStart had no linear-capable implementation at all and fell
  // through to device-controller's setVibration fallback, which is a no-op
  // (returns false) on a device with no ScalarCmd -- exactly why "Start"
  // did nothing for a stroker connected this way.

  async manualStart(speed = 50): Promise<boolean> {
    if (!this.device || !this.isConnected) return false
    this.oscillateSpeed = Math.max(0, Math.min(100, speed))
    if (!this.device.messageAttributes.LinearCmd) {
      return this.setVibration(this.oscillateSpeed)
    }
    this.oscillateDirection = 100
    this.runOscillationStep()
    return true
  }

  async manualStop(): Promise<boolean> {
    if (this.oscillateTimer) {
      clearTimeout(this.oscillateTimer)
      this.oscillateTimer = null
    }
    return this.stop()
  }

  async manualSetSpeed(speed: number): Promise<boolean> {
    this.oscillateSpeed = Math.max(0, Math.min(100, speed))
    if (!this.device?.messageAttributes.LinearCmd) {
      return this.setVibration(this.oscillateSpeed)
    }
    return true // next oscillation step picks up the new speed
  }

  async setSlideRange(min: number, max: number): Promise<boolean> {
    this.strokeMin = Math.max(0, Math.min(100, min))
    this.strokeMax = Math.max(this.strokeMin + 1, Math.min(100, max))
    return true
  }

  private runOscillationStep = () => {
    if (!this.device || !this.isConnected) return
    const range = Math.max(1, this.strokeMax - this.strokeMin)
    // Duration for a full 0-100 stroke: ~2.2s at speed 0 down to ~250ms at
    // speed 100, scaled down for a shorter configured stroke range so speed
    // reads consistently regardless of how narrow the range is set.
    const fullRangeDuration = 2200 - (this.oscillateSpeed / 100) * 1950
    const duration = Math.max(150, Math.round(fullRangeDuration * (range / 100)))
    const target = this.oscillateDirection
    this.setPosition(target, duration).catch(() => {})
    this.oscillateDirection = this.oscillateDirection === 100 ? 0 : 100
    this.oscillateTimer = setTimeout(this.runOscillationStep, duration)
  }

  startConnectionMonitor(onStatusChange: (connected: boolean) => void, intervalMs = 5000) {
    this.connectionMonitorInterval = setInterval(async () => {
      const connected = await this.checkConnection()
      if (connected !== this.isConnected) {
        this.isConnected = connected
        onStatusChange(connected)
      }
    }, intervalMs)
  }

  stopConnectionMonitor() {
    if (this.connectionMonitorInterval) {
      clearInterval(this.connectionMonitorInterval)
      this.connectionMonitorInterval = null
    }
  }

  getIsConnected(): boolean {
    return this.isConnected
  }

  disconnect() {
    this.stop()
    if (this.client) {
      this.client.disconnect()
    }
    this.device = null
    this.isClientConnected = false
    this.isConnected = false
  }

  async scanDevices(): Promise<Array<{ name: string; index: number }>> {
    this.lastError = null
    try {
      console.log("[v0] Starting Bluetooth device scan...")
      const scan = await this.openAndScan(8000)
      if (!scan.connected) return []

      if (scan.timedOut) console.log("[v0] Scan timeout reached, stopping scan")

      const devices = scan.found
        .filter((dev) => {
          console.log("[v0] Device found:", { name: dev.name, index: dev.index, attrs: dev.messageAttributes })
          // Accept strokers (LinearCmd), vibrators/misc (ScalarCmd), or rotators (RotateCmd)
          return Boolean(
            dev.messageAttributes.LinearCmd ||
            dev.messageAttributes.ScalarCmd ||
            dev.messageAttributes.RotateCmd,
          )
        })
        .map((dev) => ({ name: dev.name, index: dev.index }))

      if (devices.length === 0) {
        const seenDeviceNames = scan.found.map((dev) => dev.name)
        this.lastError = seenDeviceNames.length > 0
          ? `Connected to Intiface Central and saw ${seenDeviceNames.join(", ")}, but it doesn't support stroke, vibration, or rotation control (no LinearCmd/ScalarCmd/RotateCmd) -- that device type isn't supported here yet.`
          : "Connected to Intiface Central, but it reported zero devices within 8 seconds. This means the device itself isn't connected inside Intiface Central yet -- open Intiface Central, confirm your device shows as connected THERE first (not just plugged in/powered on), then scan again."
      }

      console.log("[v0] Scan complete. Devices found:", devices)
      return devices
    } catch (error) {
      this.lastError = error instanceof Error ? error.message : "Buttplug device scan failed for an unknown reason."
      console.error("[v0] Buttplug device scan failed:", error)
      return []
    }
  }
}
